import type { PortraitCharacter } from '../lib/characters';
import { Portrait } from './Portrait';

type PortraitFrameProps = {
  character: PortraitCharacter;
  active: boolean;
  awake: boolean;
  caption: string;
  captionIsUser: boolean;
  away?: boolean;
  videoSrc?: string | null;
  onSelect: () => void;
  onVideoPlaying?: () => void;
  onVideoEnded?: () => void;
  onVideoError?: () => void;
};

export function PortraitFrame({
  character,
  active,
  awake,
  caption,
  captionIsUser,
  away = false,
  videoSrc = null,
  onSelect,
  onVideoPlaying,
  onVideoEnded,
  onVideoError,
}: PortraitFrameProps) {
  return (
    <section
      className={`portrait-stage portrait-stage-${character.id} ${active ? 'is-selected' : ''}`}
      aria-label={`The enchanted portrait of ${character.name}`}
    >
      <svg className="hanging-wire" viewBox="0 0 100 12" preserveAspectRatio="none" aria-hidden="true">
        <path d="M1 12 L50 1.6 L99 12" />
      </svg>
      <div className="picture-nail" aria-hidden="true" />
      <div className="frame-shadow" />
      <div className="ornate-frame" onClick={onSelect} aria-hidden="true">
        <span className="frame-corner corner-tl" />
        <span className="frame-corner corner-tr" />
        <span className="frame-corner corner-bl" />
        <span className="frame-corner corner-br" />
        <div className="frame-inner">
          <Portrait
            characterId={character.id}
            imagePrefix={character.imagePrefix}
            alt={character.alt}
            awake={awake}
            away={away}
            awayPlateSrc={character.emptyPlate}
            videoSrc={videoSrc}
            onVideoPlaying={onVideoPlaying}
            onVideoEnded={onVideoEnded}
            onVideoError={onVideoError}
          />
        </div>
      </div>

      <button
        className="name-plaque"
        type="button"
        aria-label={`${active ? 'Speaking with' : 'Speak with'} ${character.name}, ${character.title}`}
        aria-pressed={active}
        onClick={onSelect}
      >
        <span className="plaque-name">{character.name}</span>
        <span className="plaque-title">{character.title}</span>
      </button>

      <div
        className={`caption ${caption ? 'is-visible' : ''} ${captionIsUser ? 'is-user' : ''}`}
        aria-live={active ? 'polite' : 'off'}
      >
        <span className="caption-flourish" aria-hidden="true">◆</span>
        <p>{caption}</p>
        <span className="caption-flourish" aria-hidden="true">◆</span>
      </div>
    </section>
  );
}
